import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	Input,
} from "@monbits/ui";
import { AxiosError } from "axios";
import { Check } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "src/components/logo";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import authProvider from "src/providers/auth-provider";
import invitationProvider from "src/providers/invitation-provider";
import { validatePassword as validatePasswordStrength } from "src/utils/password-validator";

const AcceptInvitationPage: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const token = searchParams.get("token");

	const emailId = React.useId();
	const nameId = React.useId();
	const passwordId = React.useId();
	const confirmPasswordId = React.useId();

	const [email, setEmail] = React.useState("");
	const [name, setName] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");

	const [nameError, setNameError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");

	const [isValidating, setIsValidating] = React.useState(true);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isValidToken, setIsValidToken] = React.useState(false);

	React.useEffect(() => {
		if (!token) {
			toast.error("Invalid invitation", {
				description: "No invitation token provided",
			});
			navigate("/login");
			return;
		}

		const validateToken = async () => {
			try {
				await authProvider.getCookie();
				const data = await invitationProvider.validateInvitation(token);
				setEmail(data.email);
				setIsValidToken(true);
			} catch (error) {
				console.error("Token validation error:", error);
				toast.error("Invalid invitation", {
					description:
						"This invitation link is invalid or has expired. Please request a new invitation.",
				});
				setTimeout(() => navigate("/login"), 2000);
			} finally {
				setIsValidating(false);
			}
		};

		validateToken();
	}, [token, navigate]);

	const validateName = (value: string): boolean => {
		if (!value || value.trim().length === 0) {
			setNameError("Please enter your name");
			return false;
		}
		setNameError("");
		return true;
	};

	const validatePassword = (value: string): boolean => {
		if (!value) {
			setPasswordError("Please enter a password");
			return false;
		}

		const result = validatePasswordStrength(value, email);
		if (!result.valid) {
			setPasswordError(result.errors[0]); // Show first error
			return false;
		}

		setPasswordError("");
		return true;
	};

	const validateConfirmPassword = (value: string): boolean => {
		if (!value) {
			setConfirmPasswordError("Please confirm your password");
			return false;
		}
		if (value !== password) {
			setConfirmPasswordError("Passwords do not match");
			return false;
		}
		setConfirmPasswordError("");
		return true;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const isNameValid = validateName(name);
		const isPasswordValid = validatePassword(password);
		const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

		if (!isNameValid || !isPasswordValid || !isConfirmPasswordValid) {
			return;
		}

		if (!token) {
			return;
		}

		try {
			setIsSubmitting(true);
			await invitationProvider.acceptInvitation(token, name, password);

			toast.success("Account created successfully!", {
				description: "You can now sign in with your credentials",
				icon: <Check className="h-4 w-4" />,
			});

			setTimeout(() => navigate("/login"), 1500);
		} catch (error) {
			console.error("Accept invitation error:", error);
			if (error instanceof AxiosError) {
				const message =
					error.response?.data?.message || "Failed to create account";
				toast.error("Error", { description: message });
			} else {
				toast.error("Error", {
					description: "Failed to create account. Please try again.",
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isValidating) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<SpinWithMask isLoading={true} />
			</div>
		);
	}

	if (!isValidToken) {
		return null;
	}

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="flex justify-center mb-8">
					<Logo variant="full" size="xl" />
				</div>

				<Card>
					<CardHeader>
						<CardTitle>Complete your registration</CardTitle>
						<CardDescription>
							You've been invited to join Monbits Pay. Set up your account to
							get started.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit}>
							<FieldGroup>
								<Field>
									<FieldLabel htmlFor={emailId}>Email</FieldLabel>
									<Input id={emailId} type="email" value={email} disabled />
								</Field>

								<Field>
									<FieldLabel htmlFor={nameId}>Name</FieldLabel>
									<Input
										id={nameId}
										type="text"
										placeholder="Enter your full name"
										value={name}
										onChange={(e) => {
											setName(e.target.value);
											if (nameError) validateName(e.target.value);
										}}
										onBlur={() => validateName(name)}
										className={nameError ? "border-destructive" : ""}
										required
									/>
									{nameError && (
										<FieldDescription className="text-destructive">
											{nameError}
										</FieldDescription>
									)}
								</Field>

								<Field>
									<FieldLabel htmlFor={passwordId}>Password</FieldLabel>
									<Input
										id={passwordId}
										type="password"
										placeholder="Create a password (min. 12 characters)"
										value={password}
										onChange={(e) => {
											setPassword(e.target.value);
											if (passwordError) validatePassword(e.target.value);
											if (confirmPassword)
												validateConfirmPassword(confirmPassword);
										}}
										onBlur={() => validatePassword(password)}
										className={passwordError ? "border-destructive" : ""}
										required
									/>
									<FieldDescription
										className={
											passwordError
												? "text-destructive"
												: "text-muted-foreground"
										}
									>
										{passwordError ||
											"Must include uppercase, lowercase, digit, and special character"}
									</FieldDescription>
								</Field>

								<Field>
									<FieldLabel htmlFor={confirmPasswordId}>
										Confirm Password
									</FieldLabel>
									<Input
										id={confirmPasswordId}
										type="password"
										placeholder="Confirm your password"
										value={confirmPassword}
										onChange={(e) => {
											setConfirmPassword(e.target.value);
											if (confirmPasswordError)
												validateConfirmPassword(e.target.value);
										}}
										onBlur={() => validateConfirmPassword(confirmPassword)}
										className={confirmPasswordError ? "border-destructive" : ""}
										required
									/>
									{confirmPasswordError && (
										<FieldDescription className="text-destructive">
											{confirmPasswordError}
										</FieldDescription>
									)}
								</Field>

								<Button
									type="submit"
									disabled={isSubmitting}
									className="w-full"
								>
									{isSubmitting ? (
										<span className="flex items-center gap-2">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
											Creating account...
										</span>
									) : (
										"Create Account"
									)}
								</Button>
							</FieldGroup>
						</form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default AcceptInvitationPage;
